/**
 * agentController.js — Decision engine for the Onboarding Agent.
 *
 * The controller receives context (message, step, formData, errors, context)
 * and returns a response with messages + structured actions.
 *
 * Architecture:
 *   User message → Parser (extract entities) → Controller (decide actions) → UI (render + confirm)
 *
 * No LLM required — rule-based with entity extraction.
 */

import { parseUserMessage } from './agentParser';
import {
  fillFields,
  selectSport,
  selectModules,
  searchClub,
  navigateStep,
  summarize,
  showPending,
  addContact,
  addSportEntry,
  addInvite,
  getSportLabel,
  getOrgTypeLabel,
  getFieldLabel,
  ActionType,
} from './agentActions';

/* ══════════════════════════════════════════════════════════
   INTENT DETECTION
   ══════════════════════════════════════════════════════════ */

const norm = (s) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const INTENT_PATTERNS = {
  greet: /^(oi|ola|hey|bom dia|boa tarde|boa noite|e ai|fala)/,
  help: /(ajuda|help|o que|como funciona|como faco|nao sei|nao entendi|duvida)/,
  summarize: /(resumo|resumir|o que ja|status|como esta|visao geral|revisao)/,
  pending: /(falta|pendente|obrigatorio|que falta|preciso preencher|campos faltando)/,
  skip: /(pular|skip|nao quero|depois|opcional|proximo|proxima|avancar|avançar)/,
  back: /(voltar|anterior|volta)/,
  price: /(quanto custa|preco|gratuito|gratis|pagar|plano|assinatura)/,
  security: /(seguro|seguranca|lgpd|dados|privacidade)/,
  time: /(quanto tempo|demora|rapido)/,
  edit_later: /(alterar depois|mudar depois|editar depois|corrigir)/,
  invite: /(convidar|convite|equipe|staff|membro)/,
  modules: /(modulo|servico|funcionalidade|recurso|feature)/,
  results_link: /(vincular|resultado|competicao|base oficial|link)/,
  describe_club: /(somos|nosso clube|meu clube|nós somos|nossa instituicao|nos temos|temos)/,
};

function detectIntent(text) {
  const n = norm(text);
  for (const [intent, regex] of Object.entries(INTENT_PATTERNS)) {
    if (regex.test(n)) return intent;
  }
  return 'describe_club'; // Default: try to extract entities
}

/* ══════════════════════════════════════════════════════════
   PENDING FIELDS LOGIC
   ══════════════════════════════════════════════════════════ */

function getRegisterPending(formData) {
  const required = {
    full_name: 'Nome completo',
    email: 'Email',
    password: 'Senha',
    confirmPassword: 'Confirmar senha',
    primary_sport: 'Esporte principal',
  };
  const pending = [];
  for (const [key, label] of Object.entries(required)) {
    if (!formData[key] || String(formData[key]).trim() === '') {
      pending.push({ key, label });
    }
  }
  return pending;
}

function getWizardStep1Pending(formData) {
  const required = {
    legal_name: 'Nome oficial',
    org_type: 'Tipo de organização',
    primary_sport: 'Esporte principal',
    city: 'Cidade',
  };
  const pending = [];
  for (const [key, label] of Object.entries(required)) {
    if (!formData[key] || String(formData[key]).trim() === '') {
      pending.push({ key, label });
    }
  }
  return pending;
}

/* ══════════════════════════════════════════════════════════
   SUMMARIZE LOGIC
   ══════════════════════════════════════════════════════════ */

function buildRegisterSummary(formData) {
  const lines = [];
  if (formData.full_name) lines.push(`**Nome:** ${formData.full_name}`);
  if (formData.email) lines.push(`**Email:** ${formData.email}`);
  if (formData.organization_name) lines.push(`**Clube:** ${formData.organization_name}`);
  if (formData.primary_sport) lines.push(`**Esporte:** ${getSportLabel(formData.primary_sport)}`);
  if (formData.city) lines.push(`**Cidade:** ${formData.city}`);
  if (formData.phone) lines.push(`**Telefone:** ${formData.phone}`);
  return lines.length > 0
    ? `**Dados preenchidos:**\n${lines.join('\n')}`
    : 'Nenhum campo preenchido ainda.';
}

function buildWizardSummary(formData, step) {
  const lines = [];
  if (formData.legal_name) lines.push(`**Nome oficial:** ${formData.legal_name}`);
  if (formData.trade_name) lines.push(`**Nome fantasia:** ${formData.trade_name}`);
  if (formData.org_type) lines.push(`**Tipo:** ${getOrgTypeLabel(formData.org_type)}`);
  if (formData.primary_sport) lines.push(`**Esporte:** ${getSportLabel(formData.primary_sport)}`);
  if (formData.city) lines.push(`**Cidade:** ${formData.city}${formData.state ? ` - ${formData.state}` : ''}`);
  lines.push(`\n**Etapa atual:** ${step} de 7`);
  return lines.length > 1
    ? `**Resumo do clube:**\n${lines.join('\n')}`
    : 'Nenhum dado do clube preenchido ainda.';
}

/* ══════════════════════════════════════════════════════════
   MODULE RECOMMENDATION ENGINE
   ══════════════════════════════════════════════════════════ */

function recommendModules(sport, numbersMap) {
  const base = ['results', 'rankings', 'evolution', 'dashboard_tech'];

  if (sport === 'swimming') {
    base.push('benchmarking');
  }
  if (sport === 'football') {
    base.push('video_upload', 'video_analysis');
  }

  const athleteCount = numbersMap.athletes || 0;
  if (athleteCount > 50) {
    base.push('reports', 'comparatives');
  }

  const coachCount = numbersMap.coaches || 0;
  if (coachCount >= 3) {
    base.push('builder_tech', 'session_control');
  }

  return [...new Set(base)];
}

/* ══════════════════════════════════════════════════════════
   FAQ (static)
   ══════════════════════════════════════════════════════════ */

const FAQ = {
  price: 'O cadastro é gratuito. Módulos avançados (IA, vídeo, wearables) terão planos pagos no futuro, mas você pode começar sem custo.',
  security: 'Seus dados são protegidos pela LGPD. Usamos criptografia e não compartilhamos informações pessoais.',
  time: 'O cadastro leva ~2 minutos. O setup do clube pode ser feito em 5-10 minutos, e você pode pausar a qualquer momento.',
  edit_later: 'Sim! Todas as informações podem ser alteradas depois nas configurações.',
  help: 'Posso ajudar com qualquer etapa! Me diga sobre o seu clube ou pergunte sobre a plataforma.',
};

/* ══════════════════════════════════════════════════════════
   MAIN CONTROLLER
   ══════════════════════════════════════════════════════════ */

/**
 * Process a user message and return agent response.
 *
 * @param {Object} params
 * @param {string} params.message - User's free text
 * @param {'register'|'wizard'} params.context - Current page context
 * @param {number} params.step - Current step (1-2 for register, 1-7 for wizard)
 * @param {Object} params.formData - Current form state
 * @param {Object} params.errors - Current validation errors
 * @returns {{ messages: string[], actions: Object[] }}
 */
export function processMessage({ message, context, step, formData, errors }) {
  const intent = detectIntent(message);
  const { entities } = parseUserMessage(message);
  const messages = [];
  const actions = [];

  /* ── Static intents ── */

  if (intent === 'greet') {
    messages.push('Olá! Sou o agente de onboarding da VUKZ. Me conte sobre o seu clube — esporte, cidade, tamanho da equipe — e eu preencho para você.');
    return { messages, actions };
  }

  if (FAQ[intent]) {
    messages.push(FAQ[intent]);
    return { messages, actions };
  }

  if (intent === 'summarize') {
    const summary = context === 'register'
      ? buildRegisterSummary(formData)
      : buildWizardSummary(formData, step);
    messages.push(summary);
    actions.push(summarize());
    return { messages, actions };
  }

  if (intent === 'pending') {
    const pending = context === 'register'
      ? getRegisterPending(formData)
      : getWizardStep1Pending(formData);
    if (pending.length === 0) {
      messages.push('Todos os campos obrigatórios estão preenchidos! Pode avançar.');
    } else {
      messages.push(`**Campos pendentes (${pending.length}):**\n${pending.map(p => `• ${p.label}`).join('\n')}`);
    }
    actions.push(showPending());
    return { messages, actions };
  }

  if (intent === 'skip') {
    if (context === 'wizard' && step >= 4 && step <= 6) {
      messages.push(`A etapa ${step} é opcional. Posso te levar para a próxima etapa.`);
      actions.push(navigateStep(step + 1, 'Pular etapa opcional'));
    } else if (context === 'wizard' && step < 4) {
      messages.push(`A etapa ${step} tem campos obrigatórios. Precisa preencher antes de avançar.`);
    } else {
      messages.push('Pode clicar em "Continuar" para avançar!');
    }
    return { messages, actions };
  }

  if (intent === 'back') {
    if (step > 1) {
      messages.push('Voltando para a etapa anterior.');
      actions.push(navigateStep(step - 1, 'Voltar'));
    } else {
      messages.push('Você já está na primeira etapa.');
    }
    return { messages, actions };
  }

  if (intent === 'invite') {
    if (context === 'wizard') {
      if (step !== 6) {
        messages.push('Os convites são configurados na etapa 6. Quer ir para lá?');
        actions.push(navigateStep(6, 'Ir para convites'));
      } else {
        messages.push('Me diga o email e a função da pessoa. Ex: "joao@clube.com como treinador"');
      }
    } else {
      messages.push('Você poderá convidar sua equipe depois de configurar o clube, na etapa 6.');
    }
    return { messages, actions };
  }

  if (intent === 'modules') {
    if (context === 'wizard') {
      if (step !== 5) {
        messages.push('Os módulos são escolhidos na etapa 5. Quer ir para lá?');
        actions.push(navigateStep(5, 'Ir para módulos'));
      } else {
        const sport = formData.primary_sport;
        const recommended = recommendModules(sport, {});
        messages.push(`Com base no esporte (${getSportLabel(sport)}), recomendo estes módulos:`);
        actions.push(selectModules(recommended, 'Módulos recomendados para o seu clube'));
      }
    } else {
      messages.push('Você escolherá os módulos depois de cadastrar, no setup do clube (etapa 5).');
    }
    return { messages, actions };
  }

  if (intent === 'results_link') {
    if (context === 'wizard') {
      if (step !== 4) {
        messages.push('O vínculo com resultados oficiais é feito na etapa 4. Quer ir?');
        actions.push(navigateStep(4, 'Ir para vínculo de resultados'));
      } else {
        const clubName = formData.legal_name || formData.trade_name;
        if (clubName) {
          messages.push(`Vou buscar "${clubName}" na base de resultados.`);
          actions.push(searchClub(clubName, 'Buscar clube na base oficial'));
        } else {
          messages.push('Me diga o nome do clube para buscar na base de resultados.');
        }
      }
    } else {
      messages.push('O vínculo com resultados será feito no setup do clube (etapa 4).');
    }
    return { messages, actions };
  }

  /* ── Entity-driven intents (describe_club or free text) ── */

  const hasEntities = Object.keys(entities).length > 0;

  if (hasEntities) {
    const fields = {};
    const explanations = [];

    // Map entities → form fields based on context
    if (context === 'register') {
      if (entities.sport && !formData.primary_sport) {
        fields.primary_sport = entities.sport;
        explanations.push(`Esporte: **${getSportLabel(entities.sport)}**`);
      }
      if (entities.city && !formData.city) {
        fields.city = entities.city;
        explanations.push(`Cidade: **${entities.city}**`);
      }
      if (entities.name && !formData.full_name) {
        fields.full_name = entities.name;
        explanations.push(`Nome: **${entities.name}**`);
      }
      if (entities.email && !formData.email) {
        fields.email = entities.email;
        explanations.push(`Email: **${entities.email}**`);
      }
      if (entities.phone && !formData.phone) {
        fields.phone = entities.phone;
        explanations.push(`Telefone: **${entities.phone}**`);
      }
    } else if (context === 'wizard') {
      if (entities.sport && !formData.primary_sport) {
        fields.primary_sport = entities.sport;
        explanations.push(`Esporte: **${getSportLabel(entities.sport)}**`);
      }
      if (entities.org_type && !formData.org_type) {
        fields.org_type = entities.org_type;
        explanations.push(`Tipo: **${getOrgTypeLabel(entities.org_type)}**`);
      }
      if (entities.city) {
        if (!formData.city) {
          fields.city = entities.city;
          explanations.push(`Cidade: **${entities.city}**`);
        }
        if (entities.state && !formData.state) {
          fields.state = entities.state;
          explanations.push(`Estado: **${entities.state}**`);
        }
      }
      if (entities.state && !entities.city && !formData.state) {
        fields.state = entities.state;
        explanations.push(`Estado: **${entities.state}**`);
      }
    }

    // Build actions
    if (Object.keys(fields).length > 0) {
      messages.push(`Entendi! Posso preencher:\n${explanations.join('\n')}`);
      actions.push(fillFields(fields, 'Dados extraídos da sua descrição'));

      // If sport was detected, also suggest as select action
      if (fields.primary_sport) {
        actions.push(selectSport(fields.primary_sport, `Selecionar ${getSportLabel(fields.primary_sport)}`));
      }
    }

    // Numbers → additional context messages
    if (entities.numbers && entities.numbers.length > 0) {
      const numLines = entities.numbers.map((n) => `• ${n.value} ${n.label}`);
      messages.push(`Estrutura identificada:\n${numLines.join('\n')}`);

      // If in wizard step 3 or ready for it, create sport entry suggestion
      if (context === 'wizard') {
        const sportEntry = {};
        const sport = entities.sport || formData.primary_sport;
        if (sport) sportEntry.sport_name = sport;
        for (const n of entities.numbers) {
          if (n.label === 'athletes') sportEntry.num_athletes = n.value;
          if (n.label === 'coaches') sportEntry.num_coaches = n.value;
          if (n.label === 'teams') sportEntry.num_teams = n.value;
        }
        if (entities.categories && entities.categories.length > 0) {
          sportEntry.categories = entities.categories;
        }
        if (Object.keys(sportEntry).length > 0) {
          actions.push(addSportEntry(sportEntry, 'Estrutura esportiva extraída'));
        }

        // Module recommendations based on size
        const numbersMap = {};
        entities.numbers.forEach((n) => { numbersMap[n.label] = n.value; });
        const recommended = recommendModules(sport, numbersMap);
        messages.push(`Com base no tamanho do clube, recomendo ${recommended.length} módulos.`);
        actions.push(selectModules(recommended, 'Módulos recomendados'));
      }
    }

    // Email + role → invite suggestion
    if (entities.email && entities.roles && entities.roles.length > 0 && context === 'wizard') {
      actions.push(addInvite(
        { invite_email: entities.email, invite_role: entities.roles[0] },
        `Convidar ${entities.email} como ${entities.roles[0]}`
      ));
    }

    // If no fields were extracted (all already filled), give feedback
    if (Object.keys(fields).length === 0 && (!entities.numbers || entities.numbers.length === 0)) {
      messages.push('Os campos já estão preenchidos com esses dados. Precisa alterar algo?');
    }

    return { messages, actions };
  }

  /* ── Fallback ── */
  messages.push(
    'Não consegui extrair dados específicos. Tente me descrever seu clube assim:\n\n' +
    '*"Somos um clube de natação de Belo Horizonte com 5 treinadores e 120 atletas"*\n\n' +
    'Ou pergunte: resumo, pendências, módulos, convites, preço, segurança.'
  );

  return { messages, actions };
}

/* ══════════════════════════════════════════════════════════
   GREETING MESSAGES
   ══════════════════════════════════════════════════════════ */

export function getGreeting(context) {
  if (context === 'register') {
    return [
      'Olá! Sou o agente de onboarding da VUKZ.',
      'Me conte sobre o seu clube em uma frase — esporte, cidade, tamanho — e eu preencho os campos para você.',
      'Exemplo: *"Somos um clube de natação de São Paulo com 80 atletas"*',
    ];
  }
  return [
    'Olá! Sou o agente de onboarding da VUKZ.',
    'Posso preencher campos, recomendar módulos, buscar seu clube na base e guiar cada etapa.',
    'Me descreva seu clube ou pergunte: *resumo, pendências, módulos, convites*',
  ];
}

/* ══════════════════════════════════════════════════════════
   STEP CHANGE MESSAGES
   ══════════════════════════════════════════════════════════ */

export function getStepMessage(step) {
  const tips = {
    1: '**Etapa 1 — Dados da instituição.** Me diga o nome, tipo e localização do clube que eu preencho.',
    2: '**Etapa 2 — Contatos-chave.** Quem é o responsável, coordenador técnico, admin?',
    3: '**Etapa 3 — Estrutura esportiva.** Quantos atletas, categorias, treinadores?',
    4: '**Etapa 4 — Vínculo de resultados.** Posso buscar seu clube na base oficial. Opcional!',
    5: '**Etapa 5 — Módulos.** Posso recomendar com base no seu esporte e tamanho.',
    6: '**Etapa 6 — Convites.** Me diga email + função e eu crio o convite. Opcional!',
    7: '**Etapa 7 — Revisão.** Diga "resumo" para ver tudo antes de ativar.',
  };
  return tips[step] || '';
}

/* ══════════════════════════════════════════════════════════
   ERROR TIPS
   ══════════════════════════════════════════════════════════ */

export function getErrorTip(errorKey) {
  const tips = {
    full_name: 'O nome precisa ter pelo menos 2 caracteres.',
    email: 'Formato de email inválido. Confira se tem @ e domínio.',
    password: 'A senha precisa ter pelo menos 6 caracteres.',
    confirmPassword: 'As senhas não coincidem.',
    primary_sport: 'Selecione o esporte principal.',
    acceptTerms: 'Precisa aceitar os termos para continuar.',
    legal_name: 'O nome oficial da instituição é obrigatório.',
    city: 'Informe a cidade do clube.',
    general: 'Verifique os campos destacados e tente novamente.',
  };
  return tips[errorKey] || `Verifique o campo "${getFieldLabel(errorKey)}".`;
}

/* ══════════════════════════════════════════════════════════
   FIELD FOCUS TIPS
   ══════════════════════════════════════════════════════════ */

export function getFieldTip(fieldName) {
  const tips = {
    full_name: 'Seu nome completo — aparecerá como responsável pelo clube.',
    email: 'Use o email corporativo do clube, se possível.',
    password: 'Mínimo 6 caracteres. Quanto mais longa, melhor.',
    confirmPassword: 'Repita a mesma senha.',
    organization_name: 'Opcional agora — pode definir no setup.',
    phone: 'Telefone do clube ou pessoal.',
    city: 'Cidade sede do clube.',
    country: 'País do clube.',
    primary_sport: 'Esporte principal. Pode adicionar mais no setup.',
    legal_name: 'Nome oficial registrado da instituição.',
    trade_name: 'Nome pelo qual o clube é conhecido.',
    org_type: 'Clube, academia, federação, equipe...',
    cnpj: 'CNPJ é opcional, mas ajuda na verificação.',
    state: 'Estado (UF) do clube.',
    website: 'Site oficial, se houver.',
    instagram: '@ do Instagram do clube.',
  };
  return tips[fieldName] || null;
}
